// src/pages/CommandesCours.jsx
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, Modal, Form, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function CommandesCours() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('clients');
    const [selectedCommande, setSelectedCommande] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailType, setDetailType] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    /* ============================================================
       PAGINATION
       ============================================================ */
    const paginateData = (dataArray, page, itemsPerPage) => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return dataArray?.slice(startIndex, endIndex) || [];
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getTotalPages = (dataLength) => Math.ceil((dataLength || 0) / itemsPerPage);

    const PaginationComponent = ({ currentPage, totalPages, onPageChange }) => {
        if (totalPages <= 1) return null;

        const getPageNumbers = () => {
            const pages = [];
            const maxVisible = 5;
            let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
            let end = Math.min(totalPages, start + maxVisible - 1);
            if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        };

        return (
            <div className="pagination-wrapper-charte">
                <div className="pagination-info-charte">
                    Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                </div>
                <ul className="pagination-charte mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>
                            <i className="ri-arrow-left-s-line"></i>
                        </button>
                    </li>
                    {getPageNumbers().map(page => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => onPageChange(page)}>
                                {page}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>
                            <i className="ri-arrow-right-s-line"></i>
                        </button>
                    </li>
                </ul>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Lignes :</span>
                    <Form.Select
                        className="filter-select-mini-charte"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </Form.Select>
                </div>
            </div>
        );
    };

    /* ============================================================
       FETCH
       ============================================================ */
    useEffect(() => {
        fetchCommandesCours();
        let interval;
        if (autoRefresh) interval = setInterval(fetchCommandesCours, 60000);
        return () => clearInterval(interval);
    }, [autoRefresh]);

    const fetchCommandesCours = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/commandes_cours.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = async (type, id) => {
        try {
            setLoadingDetails(true);
            const token = localStorage.getItem("token");
            const action = type === 'fournisseur' ? 'detail_fournisseur' : 'detail_client';
            const response = await fetch(
                `${API_BASE_URL}/commandes_cours.php?action=${action}&id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            const commandeDetails = result.commande || result;
            setSelectedCommande(Array.isArray(commandeDetails) ? commandeDetails : [commandeDetails]);
            setDetailType(type);
            setShowDetailModal(true);
        } catch (err) {
            console.error("Erreur chargement détails:", err);
            alert("Erreur lors du chargement des détails");
        } finally {
            setLoadingDetails(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutConfig = (statut) => {
        const configs = {
            'emise':            { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'À encaisser',           icon: 'ri-time-line' },
            'payee_partielle':  { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', text: 'Partiellement payée',   icon: 'ri-indeterminate-circle-line' },
            'envoyee':          { gradient: 'linear-gradient(135deg, #295AA7, #1e3f7a)', text: 'Envoyée',               icon: 'ri-send-plane-line' },
            'recue_partielle':  { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'Réception partielle',   icon: 'ri-inbox-line' },
            'complete':         { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Complète',              icon: 'ri-checkbox-circle-line' },
            'partielle':        { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'Partielle',             icon: 'ri-indeterminate-circle-line' },
            'en_attente':       { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: 'En attente',            icon: 'ri-time-line' }
        };
        return configs[statut] || { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: statut, icon: 'ri-question-line' };
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.text}
            </span>
        );
    };

    const getPrioriteConfig = (priorite) => {
        const configs = {
            'expiree': { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Expirée', icon: 'ri-alert-line' },
            'urgente': { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'Urgente', icon: 'ri-alarm-warning-line' },
            'normale': { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Normale', icon: 'ri-check-line' }
        };
        return configs[priorite] || configs.normale;
    };

    const getPrioriteBadge = (priorite) => {
        const config = getPrioriteConfig(priorite);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.text}
            </span>
        );
    };

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-shopping-cart-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des commandes
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchCommandesCours} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       PAGINATION DATA
       ============================================================ */
    const paginatedClients = paginateData(data?.commandes_clients, currentPage, itemsPerPage);
    const totalClientsPages = getTotalPages(data?.commandes_clients?.length);
    const paginatedFournisseurs = paginateData(data?.commandes_fournisseurs, currentPage, itemsPerPage);
    const totalFournisseursPages = getTotalPages(data?.commandes_fournisseurs?.length);
    const paginatedEcheances = paginateData(data?.echeances, currentPage, itemsPerPage);
    const totalEcheancesPages = getTotalPages(data?.echeances?.length);
    const paginatedReceptions = paginateData(data?.receptions, currentPage, itemsPerPage);
    const totalReceptionsPages = getTotalPages(data?.receptions?.length);

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-shopping-cart-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Suivi des commandes</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-user-line"></i> Clients {data?.statistiques?.nb_commandes_clients || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-truck-line"></i> Fournisseurs {data?.statistiques?.nb_commandes_fournisseurs || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-alert-line"></i> Expirées {data?.statistiques?.nb_factures_expirees || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <div className="form-check form-switch d-flex align-items-center me-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    id="autoRefresh"
                                />
                                <label className="form-check-label small ms-2" htmlFor="autoRefresh" style={{ color: '#64748b' }}>
                                    Auto
                                </label>
                            </div>
                            <Button className="btn-modern-primary" onClick={fetchCommandesCours} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-truck-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Com. fournisseurs</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.primary }}>
                                        {data?.statistiques?.nb_commandes_fournisseurs || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">{formatCurrency(data?.statistiques?.valeur_commandes_fournisseurs || 0)}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-file-list-3-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Commandes clients</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.success }}>
                                        {data?.statistiques?.nb_commandes_clients || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">{formatCurrency(data?.statistiques?.valeur_commandes_clients || 0)}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Factures expirées</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.danger }}>
                                        {data?.statistiques?.nb_factures_expirees || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">{formatCurrency(data?.statistiques?.montant_factures_expirees || 0)}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-inbox-archive-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">En attente</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.warning }}>
                                        {data?.receptions?.length || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">commandes partielles</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== TABS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="tabs-charte">
                                {[
                                    { key: 'clients', label: 'Commandes clients', icon: 'ri-user-line', badge: data?.commandes_clients?.length || 0 },
                                    { key: 'fournisseurs', label: 'Commandes fournisseurs', icon: 'ri-truck-line', badge: data?.commandes_fournisseurs?.length || 0 },
                                    { key: 'echeances', label: 'Échéances', icon: 'ri-calendar-check-line', badge: data?.echeances?.length || 0 },
                                    { key: 'receptions', label: 'Réceptions attendues', icon: 'ri-inbox-archive-line', badge: data?.receptions?.length || 0 }
                                ].map((tab) => (
                                    <button
                                        key={tab.key}
                                        className={`tab-btn-charte ${activeTab === tab.key ? 'active' : ''}`}
                                        onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                                    >
                                        <i className={tab.icon}></i>
                                        <span>{tab.label}</span>
                                        <span className="tab-count-charte">{tab.badge}</span>
                                    </button>
                                ))}
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">

                            {/* ===== TAB CLIENTS ===== */}
                            {activeTab === 'clients' && (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">N° Facture</th>
                                                    <th>Client</th>
                                                    <th>Échéance</th>
                                                    <th className="text-end">Montant</th>
                                                    <th className="text-end">Réglé</th>
                                                    <th className="text-end">Reste</th>
                                                    <th className="text-center">Priorité</th>
                                                    <th className="text-center">Statut</th>
                                                    <th className="text-center pe-4">Détails</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedClients?.map((commande) => {
                                                    const reste = commande.montant_ttc - commande.montant_regle;
                                                    const isExpired = commande.priorite === 'expiree';
                                                    return (
                                                        <tr key={commande.id_facture} className={isExpired ? 'row-late-charte' : ''}>
                                                            <td className="ps-4">
                                                                <Link to={`/ventes/facturesDetails/${commande.id_facture}`} className="table-link-charte">
                                                                    {commande.numero_facture}
                                                                </Link>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                                    {commande.client}
                                                                </div>
                                                                <small className="text-muted">{commande.code_client}</small>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {formatDate(commande.date_echeance)}
                                                                </div>
                                                                <small className={commande.jours_restants < 0 ? 'text-danger' : 'text-muted'} style={{ fontSize: '0.7rem' }}>
                                                                    {commande.jours_restants < 0
                                                                        ? `Retard de ${Math.abs(commande.jours_restants)}j`
                                                                        : `Dans ${commande.jours_restants}j`}
                                                                </small>
                                                            </td>
                                                            <td className="text-end fw-semibold">{formatCurrency(commande.montant_ttc)}</td>
                                                            <td className="text-end" style={{ color: COLORS.success }}>
                                                                {formatCurrency(commande.montant_regle)}
                                                            </td>
                                                            <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                                {formatCurrency(reste)}
                                                            </td>
                                                            <td className="text-center">{getPrioriteBadge(commande.priorite)}</td>
                                                            <td className="text-center">{getStatutBadge(commande.statut)}</td>
                                                            <td className="text-center pe-4">
                                                                <ActionButton
                                                                    icon="ri-eye-line"
                                                                    onClick={() => handleViewDetails('client', commande.id_facture)}
                                                                    tooltip="Voir les détails"
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <PaginationComponent currentPage={currentPage} totalPages={totalClientsPages} onPageChange={handlePageChange} />
                                </>
                            )}

                            {/* ===== TAB FOURNISSEURS ===== */}
                            {activeTab === 'fournisseurs' && (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">N° Commande</th>
                                                    <th>Date</th>
                                                    <th>Fournisseur</th>
                                                    <th>Livraison prévue</th>
                                                    <th className="text-end">Montant</th>
                                                    <th>Réception</th>
                                                    <th className="text-center">Statut</th>
                                                    <th className="text-center pe-4">Détails</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedFournisseurs?.map((commande) => {
                                                    const progress = (commande.quantite_recue / commande.quantite_totale) * 100;
                                                    const isLate = commande.date_livraison_prevue < new Date().toISOString().split('T')[0];
                                                    return (
                                                        <tr key={commande.id_commande_fournisseur}>
                                                            <td className="ps-4">
                                                                <Link to={`/achats/commandes/${commande.id_commande_fournisseur}`} className="table-link-charte">
                                                                    {commande.numero_commande}
                                                                </Link>
                                                            </td>
                                                            <td className="text-muted">{formatDate(commande.date_commande)}</td>
                                                            <td>
                                                                <div className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                                    {commande.fournisseur}
                                                                </div>
                                                                <small className="text-muted">{commande.code_fournisseur}</small>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {formatDate(commande.date_livraison_prevue)}
                                                                </div>
                                                                {isLate && (
                                                                    <small className="text-danger">
                                                                        <i className="ri-alert-line me-1"></i>En retard
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td className="text-end fw-semibold">{formatCurrency(commande.montant_ttc)}</td>
                                                            <td style={{ minWidth: '160px' }}>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="progress-charte" style={{ width: '80px' }}>
                                                                        <div className="progress-bar-charte" style={{
                                                                            width: `${progress}%`,
                                                                            background: `linear-gradient(90deg, ${COLORS.success}, #059669)`
                                                                        }}></div>
                                                                    </div>
                                                                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                        {commande.quantite_recue}/{commande.quantite_totale}
                                                                    </small>
                                                                </div>
                                                                <div className="mt-1">
                                                                    {getStatutBadge(commande.etat_reception)}
                                                                </div>
                                                            </td>
                                                            <td className="text-center">{getStatutBadge(commande.statut)}</td>
                                                            <td className="text-center pe-4">
                                                                <ActionButton
                                                                    icon="ri-eye-line"
                                                                    onClick={() => handleViewDetails('fournisseur', commande.id_commande_fournisseur)}
                                                                    tooltip="Voir les détails"
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <PaginationComponent currentPage={currentPage} totalPages={totalFournisseursPages} onPageChange={handlePageChange} />
                                </>
                            )}

                            {/* ===== TAB ÉCHÉANCES ===== */}
                            {activeTab === 'echeances' && (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Facture</th>
                                                    <th>Client</th>
                                                    <th>Date facture</th>
                                                    <th>Date échéance</th>
                                                    <th className="text-end">Montant dû</th>
                                                    <th className="text-center">Jours restants</th>
                                                    <th className="text-center pe-4">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedEcheances?.map((echeance) => (
                                                    <tr key={echeance.id_facture}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge-charte">
                                                                <i className="ri-barcode-line"></i>{echeance.numero_facture}
                                                            </span>
                                                        </td>
                                                        <td className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                            {echeance.client}
                                                        </td>
                                                        <td className="text-muted">{formatDate(echeance.date_facture)}</td>
                                                        <td className={echeance.jours_restants < 0 ? 'text-danger fw-semibold' : 'text-muted'}>
                                                            {formatDate(echeance.date_echeance)}
                                                        </td>
                                                        <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(echeance.montant_du)}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="status-badge-charte" style={{
                                                                background: echeance.jours_restants < 0
                                                                    ? `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                                                                    : echeance.jours_restants <= 3
                                                                    ? `linear-gradient(135deg, ${COLORS.warning}, #d97706)`
                                                                    : `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                                            }}>
                                                                <i className="ri-time-line"></i>
                                                                {echeance.jours_restants < 0
                                                                    ? `Retard ${Math.abs(echeance.jours_restants)}j`
                                                                    : `J-${echeance.jours_restants}`}
                                                            </span>
                                                        </td>
                                                        <td className="text-center pe-4">
                                                            <ActionButton
                                                                icon="ri-bank-card-line"
                                                                onClick={() => navigate(`/ventes/facturesDetails/${echeance.id_facture}`)}
                                                                tooltip="Encaisser"
                                                                color={COLORS.success}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <PaginationComponent currentPage={currentPage} totalPages={totalEcheancesPages} onPageChange={handlePageChange} />
                                </>
                            )}

                            {/* ===== TAB RÉCEPTIONS ===== */}
                            {activeTab === 'receptions' && (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th className="ps-4">Commande</th>
                                                    <th>Fournisseur</th>
                                                    <th>Livraison prévue</th>
                                                    <th className="text-end">Produits</th>
                                                    <th className="text-end">Qté restante</th>
                                                    <th className="text-center pe-4">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedReceptions?.map((reception) => {
                                                    const isLate = reception.date_livraison_prevue < new Date().toISOString().split('T')[0];
                                                    return (
                                                        <tr key={reception.id_commande_fournisseur}>
                                                            <td className="ps-4">
                                                                <span className="reference-badge-charte">
                                                                    <i className="ri-barcode-line"></i>{reception.numero_commande}
                                                                </span>
                                                            </td>
                                                            <td className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                                {reception.fournisseur}
                                                            </td>
                                                            <td className={isLate ? 'text-danger fw-semibold' : 'text-muted'}>
                                                                {formatDate(reception.date_livraison_prevue)}
                                                                {isLate && <i className="ri-alert-line ms-1"></i>}
                                                            </td>
                                                            <td className="text-end">{reception.nb_produits}</td>
                                                            <td className="text-end fw-bold" style={{ color: COLORS.warning }}>
                                                                {formatNumber(reception.quantite_restante)}
                                                            </td>
                                                            <td className="text-center pe-4">
                                                                <ActionButton
                                                                    icon="ri-check-line"
                                                                    onClick={() => navigate(`/achats/commandes/${reception.id_commande_fournisseur}`)}
                                                                    tooltip="Réceptionner"
                                                                    color={COLORS.success}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <PaginationComponent currentPage={currentPage} totalPages={totalReceptionsPages} onPageChange={handlePageChange} />
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL DÉTAIL ===== */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className={detailType === 'fournisseur' ? 'ri-truck-line' : 'ri-user-line'}></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">
                                {detailType === 'fournisseur' ? 'Détail commande fournisseur' : 'Détail commande client'}
                            </Modal.Title>
                            <p className="text-white-50 mb-0 small">Informations détaillées</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {loadingDetails ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: COLORS.primary }} />
                            <p className="mt-3 text-muted small">Chargement des détails...</p>
                        </div>
                    ) : selectedCommande && selectedCommande.length > 0 ? (
                        <>
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-shopping-bag-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Total articles</span>
                                            <div className="stat-value-charte">{selectedCommande.length}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-stack-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Quantité totale</span>
                                            <div className="stat-value-charte">
                                                {selectedCommande.reduce((acc, item) => acc + (item.quantite || item.quantite_commandee || 0), 0)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <Table className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th>Produit</th>
                                            <th className="text-center">Quantité</th>
                                            <th className="text-end">Prix unitaire</th>
                                            <th className="text-end">Total HT</th>
                                            {detailType === 'fournisseur' && <th className="text-center pe-4">Reçu</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedCommande.map((ligne, idx) => {
                                            const isComplete = (ligne.quantite_recue || 0) >= (ligne.quantite_commandee || ligne.quantite);
                                            return (
                                                <tr key={idx}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge-charte">
                                                            <i className="ri-barcode-line"></i>{ligne.reference}
                                                        </span>
                                                    </td>
                                                    <td className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                        {ligne.designation}
                                                    </td>
                                                    <td className="text-center">{ligne.quantite || ligne.quantite_commandee}</td>
                                                    <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                        {formatCurrency(ligne.montant_ligne_ht)}
                                                    </td>
                                                    {detailType === 'fournisseur' && (
                                                        <td className="text-center pe-4">
                                                            <span className="status-badge-charte" style={{
                                                                background: isComplete
                                                                    ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                                                    : `linear-gradient(135deg, ${COLORS.warning}, #d97706)`
                                                            }}>
                                                                <i className={isComplete ? 'ri-check-line' : 'ri-time-line'}></i>
                                                                {ligne.quantite_recue || 0}/{ligne.quantite_commandee || ligne.quantite}
                                                            </span>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    ) : (
                        <div className="empty-state-charte">
                            <div className="empty-state-icon-charte">
                                <i className="ri-inbox-line"></i>
                            </div>
                            <h6 className="fw-semibold mb-1">Aucun détail</h6>
                            <p className="text-muted small mb-0">Aucun détail disponible</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDetailModal(false)} className="btn-modern-outline">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== TABS ===== */
                .tabs-charte {
                    display: flex;
                    gap: 0.25rem;
                    flex-wrap: wrap;
                }
                .tab-btn-charte {
                    border: none;
                    background: transparent;
                    color: rgba(255,255,255,0.7);
                    padding: 0.6rem 1rem;
                    border-radius: 10px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .tab-btn-charte:hover {
                    background: rgba(255,255,255,0.1);
                    color: white;
                }
                .tab-btn-charte.active {
                    background: white;
                    color: var(--primary);
                    font-weight: 600;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                .tab-count-charte {
                    background: ${COLORS.primary}20;
                    color: var(--primary);
                    border-radius: 50px;
                    padding: 0.1rem 0.5rem;
                    font-size: 0.65rem;
                    font-weight: 700;
                    min-width: 20px;
                    text-align: center;
                }
                .tab-btn-charte.active .tab-count-charte {
                    background: var(--primary);
                    color: white;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte { background: #fef2f2; }
                .table-charte tbody tr.row-late-charte:hover { background: #fee2e2; }

                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== PROGRESS ===== */
                .progress-charte {
                    height: 6px; border-radius: 4px;
                    background: #f1f5f9;
                    overflow: hidden;
                }
                .progress-bar-charte {
                    height: 100%; border-radius: 4px;
                    transition: width 0.6s ease;
                }

                /* ===== STAT ITEM ===== */
                .stat-item-charte { display: flex; align-items: center; gap: 0.75rem; }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .stat-label-charte {
                    display: block; font-size: 0.7rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
                }
                .stat-value-charte { font-size: 1.15rem; font-weight: 700; color: #1e293b; }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { display: flex; gap: 6px; list-style: none; padding: 0; }
                .pagination-charte .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                    background: white;
                    cursor: pointer;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }
                .pagination-charte .page-item.disabled .page-link {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
                .filter-select-mini-charte {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.35rem 0.6rem;
                    font-size: 0.8rem;
                    width: 80px;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTE ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== FORM CHECK ===== */
                .form-check-input:checked {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .tab-btn-charte { font-size: 0.7rem; padding: 0.5rem 0.6rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}